import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { concat, Observable, of } from 'rxjs';
import { filter, flatMap, switchMap } from 'rxjs/operators';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { DialogService } from '../dialog/dialog.service';
import {
  ADD_APPOINTMENT_QUERY_PARAM,
  ADD_CLIENT_QUERY_PARAM,
  DOCUMENTS_NEW_ROUTE,
} from '../document-data/document-data.data';
import {
  ActionsEnum as EntityActionsEnum,
  clearList,
  ClearList,
  DeleteEntityNetworkSuccess,
  SaveEntityNetworkSuccess,
  SetFiltersFetched,
  setFiltersFetched,
  setListFetched,
  SetListFetched,
} from '../entity-data/entity-data.actions';
import { ALL_LIST_FILTER } from '../entity-data/entity-data.data';
import { EntityDataEffects } from '../entity-data/entity-data.effects';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { INVOICES_MASTER_ROUTE } from '../main/main.data';
import { NavigationService } from '../navigation/navigation.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { ActionsEnum, BillAppointment } from './appointment-data.actions';
import { AppointmentDataBuilder } from './appointment-data.builder';
import { IState } from './appointment-data.reducer';
import { AppointmentDataService } from './appointment-data.service';
import { AppointmentDataStore } from './appointment-data.store';
import { Appointment } from './models/appointment.model';
import { EntityTypesEnum } from '../../app.enum';
import { AppStore } from '../../store';

@Injectable()
export class AppointmentDataEffects extends EntityDataEffects<
  IState,
  Appointment
> {
  constructor(
    appointmentDataService: AppointmentDataService,
    appointmentDataStore: AppointmentDataStore,
    appointmentDataBuilder: AppointmentDataBuilder,
    actions: Actions,
    appStore: AppStore,
    translateService: TranslateService,
    globalBannerService: GlobalBannerService,
    dialogService: DialogService,
    navigationService: NavigationService,
    settingsDataStore: SettingsDataStore,
    featuresDataStore: FeaturesDataStore,
    localStorageService: LocalStorageService,
    featuresDataDispatcher: FeaturesDataDispatcher
  ) {
    super(
      actions,
      appStore,
      appointmentDataService,
      appointmentDataStore,
      appointmentDataBuilder,
      settingsDataStore,
      featuresDataStore,
      translateService,
      globalBannerService,
      dialogService,
      navigationService,
      localStorageService,
      featuresDataDispatcher
    );
  }

  @Effect() loadSearchParams = this.loadSearchParams;
  @Effect() persistSearchParams = this.persistSearchParams;
  @Effect() fetchList = this.fetchList;
  @Effect() searchList = this.searchList;
  @Effect() fetchNextPage = this.fetchNextPage;
  @Effect() fetchFilters = this.fetchFilters;
  @Effect() triggerRefreshFiltersWithDelay = this
    .triggerRefreshFiltersWithDelay;
  @Effect() getEntityById = this.getEntityById;
  @Effect() saveNewEntity = this.saveNewEntity;
  @Effect() saveNewEntityError = this.saveNewEntityError;
  @Effect() updateEntity = this.updateEntity;
  @Effect() createNewEditingEntity = this.createNewEditingEntity;
  @Effect() refreshEditingEntity = this.refreshEditingEntity;
  @Effect() setEditingEntityContent = this.setEditingEntityContent;
  @Effect() setEditingEntityProperties = this.setEditingEntityProperties;
  @Effect() setEditingEntityField = this.setEditingEntityField;
  @Effect() saveEditingEntity = this.saveEditingEntity;
  @Effect() saveExistingEntityError = this.saveExistingEntityError;
  @Effect() saveEntityStore = this.saveEntityStore;
  @Effect() deleteEntityStore = this.deleteEntityStore;
  @Effect() saveEntityToNetwork = this.saveEntityToNetwork;
  @Effect() deleteEntityNetwork = this.deleteEntityNetwork;

  /**
   * Bill appointment
   */
  @Effect()
  billAppointment: Observable<
    ClearList | SetFiltersFetched
  > = this.actions.pipe(
    ofType(ActionsEnum.BILL_APPOINTMENT),
    flatMap(({ payload }: BillAppointment) => {
      const { appointmentId, clientReferenceId } = payload;
      const queryParams = {
        [ADD_CLIENT_QUERY_PARAM]: clientReferenceId,
        [ADD_APPOINTMENT_QUERY_PARAM]: appointmentId,
      };

      this.navigationService.goTo(
        [INVOICES_MASTER_ROUTE, DOCUMENTS_NEW_ROUTE],
        { queryParams }
      );

      // TODO [refactor]: once documents've been refactored, clear lists & filters after invoice has been saved/deleted
      return concat([
        clearList(this.entityType, ALL_LIST_FILTER),
        setFiltersFetched(this.entityType, false, ALL_LIST_FILTER),
      ]);
    })
  );

  @Effect()
  clearListAfterClientUpdates: Observable<SetListFetched> = this.actions.pipe(
    ofType(
      EntityActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS,
      EntityActionsEnum.DELETE_ENTITY_NETWORK_SUCCESS
    ),
    filter(
      ({ payload }: SaveEntityNetworkSuccess | DeleteEntityNetworkSuccess) => {
        return payload.entityType === EntityTypesEnum.client;
      }
    ),
    switchMap(() => of(setListFetched(this.entityType, false, ALL_LIST_FILTER)))
  );
}
